import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { 
  Activity, TrendingUp, TrendingDown, RefreshCw, Download, 
  Trophy, Filter, Brain, Clock, AlertTriangle, CheckCircle,
  XCircle, Zap, BarChart3, Target, Layers
} from 'lucide-react';
import * as API from '../../lib/api';
import { toast } from 'sonner';

const LEVEL_COLORS = {
  INFO: 'text-blue-400',
  WARN: 'text-yellow-400',
  DECISION: 'text-purple-400',
  ERROR: 'text-red-400',
  DEBUG: 'text-zinc-500',
};

const TYPE_ICONS = {
  analysis_created: Activity,
  consensus_started: Brain,
  consensus_layer_complete: Layers,
  consensus_gate_failed: XCircle,
  consensus_complete: CheckCircle,
  paper_trade_generated: Target,
  paper_trade_resolved: Trophy,
  llm_call: Zap,
  scorecard_update: BarChart3,
};

// Match backend MIN_DECIDED_FOR_WIN_RATE_SIGNAL — neutral until enough decided trades
const MIN_DECIDED_FOR_SIGNAL = 10;

/**
 * Win-rate display color: neutral until min sample; then green/amber/red on rate.
 * Never paint early/small samples as "success green".
 */
function winRateColorClass(winRate, decidedCount, minDecided = MIN_DECIDED_FOR_SIGNAL) {
  const decided = Number(decidedCount) || 0;
  if (decided < minDecided) return 'text-zinc-400';
  const rate = Number(winRate) || 0;
  if (rate >= 55) return 'text-green-400';
  if (rate >= 45) return 'text-zinc-300';
  return 'text-red-400';
}

function decidedFromSummary(summary) {
  if (!summary) return 0;
  if (typeof summary.decided_trades === 'number') return summary.decided_trades;
  return (Number(summary.wins) || 0) + (Number(summary.losses) || 0);
}

export default function AdminPanel() {
  const [activityLogs, setActivityLogs] = useState([]);
  const [activitySummary, setActivitySummary] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [allTrades, setAllTrades] = useState({ trades: [], total: 0 });
  const [loading, setLoading] = useState({});
  
  // Filters
  const [logFilter, setLogFilter] = useState({ sinceHours: 24, level: 'all' });
  const [leaderboardFilter, setLeaderboardFilter] = useState({ sortBy: 'risk_reward_ratio', status: 'open', minConfidence: 0 });
  const [tradeFilter, setTradeFilter] = useState({ status: '', direction: '', symbol: '' });

  const loadActivityLogs = useCallback(async () => {
    setLoading(prev => ({ ...prev, logs: true }));
    try {
      const data = await API.getActivityLogs({
        limit: 200,
        sinceHours: logFilter.sinceHours,
        levels: logFilter.level !== 'all' ? logFilter.level : undefined,
      });
      setActivityLogs(data.logs || []);
    } catch (err) {
      console.error('Failed to load activity logs:', err);
    } finally {
      setLoading(prev => ({ ...prev, logs: false }));
    }
  }, [logFilter]);

  const loadActivitySummary = useCallback(async () => {
    try {
      const data = await API.getActivitySummary(24);
      setActivitySummary(data);
    } catch (err) {
      console.error('Failed to load activity summary:', err);
    }
  }, []);

  const loadScorecard = useCallback(async () => {
    setLoading(prev => ({ ...prev, scorecard: true }));
    try {
      const data = await API.getScorecard();
      setScorecard(data);
    } catch (err) {
      console.error('Failed to load scorecard:', err);
    } finally {
      setLoading(prev => ({ ...prev, scorecard: false }));
    }
  }, []);

  const loadLeaderboard = useCallback(async () => {
    setLoading(prev => ({ ...prev, leaderboard: true }));
    try {
      const data = await API.getLeaderboard(leaderboardFilter);
      setLeaderboard(data.trades || []);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    } finally {
      setLoading(prev => ({ ...prev, leaderboard: false }));
    }
  }, [leaderboardFilter]);

  const loadAllTrades = useCallback(async () => {
    setLoading(prev => ({ ...prev, trades: true }));
    try {
      const data = await API.getAllTrades({
        ...tradeFilter,
        limit: 100,
      });
      setAllTrades(data);
    } catch (err) {
      console.error('Failed to load trades:', err);
    } finally {
      setLoading(prev => ({ ...prev, trades: false }));
    }
  }, [tradeFilter]);

  const handleRefreshScorecard = async () => {
    setLoading(prev => ({ ...prev, scorecard: true }));
    try {
      const data = await API.refreshScorecard();
      setScorecard(data);
      toast.success('Scorecard refreshed');
    } catch (err) {
      toast.error('Failed to refresh scorecard');
    } finally {
      setLoading(prev => ({ ...prev, scorecard: false }));
    }
  };

  const handleExportTrades = async () => {
    try {
      const data = await API.exportTrades({ format: 'json' });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paper_trades_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.total_count} trades`);
    } catch (err) {
      toast.error('Failed to export trades');
    }
  };

  useEffect(() => {
    loadActivityLogs();
    loadActivitySummary();
    loadScorecard();
    loadLeaderboard();
    loadAllTrades();
  }, []);

  useEffect(() => {
    loadActivityLogs();
  }, [logFilter, loadActivityLogs]);

  useEffect(() => {
    loadLeaderboard();
  }, [leaderboardFilter, loadLeaderboard]);

  useEffect(() => {
    loadAllTrades();
  }, [tradeFilter, loadAllTrades]);

  const formatTime = (ts) => {
    if (!ts) return '--';
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatPrice = (price) => {
    if (!price) return '--';
    return Number(price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  const summary = scorecard?.summary;
  const decidedCount = decidedFromSummary(summary);
  const winRate = summary?.win_rate ?? 0;
  const winRateClass = winRateColorClass(winRate, decidedCount, summary?.min_decided_for_signal ?? MIN_DECIDED_FOR_SIGNAL);
  const expiredCount = summary?.expired ?? 0;

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#14161a] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">Total Trades</p>
                <p className="text-2xl font-bold text-white">{scorecard?.summary?.total_trades || 0}</p>
              </div>
              <Target className="w-8 h-8 text-[#f5b400]/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#14161a] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">Win Rate</p>
                <p className={`text-2xl font-bold ${winRateClass}`} data-testid="summary-win-rate">
                  {winRate}%
                </p>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  {decidedCount > 0
                    ? `${summary?.wins || 0}W / ${summary?.losses || 0}L decided`
                    : 'No decided trades yet'}
                  {expiredCount > 0 ? ` · ${expiredCount} expired` : ''}
                </p>
              </div>
              <Trophy className={`w-8 h-8 ${decidedCount < (summary?.min_decided_for_signal ?? MIN_DECIDED_FOR_SIGNAL) ? 'text-zinc-500/50' : 'text-zinc-400/50'}`} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#14161a] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">Open Trades</p>
                <p className="text-2xl font-bold text-blue-400">{scorecard?.summary?.open_trades || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#14161a] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">24h Activities</p>
                <p className="text-2xl font-bold text-purple-400">{activitySummary?.total_activities || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="activity" className="space-y-4">
        <TabsList className="bg-[#14161a] border border-white/5 rounded-sm p-1">
          <TabsTrigger value="activity" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-activity">
            <Activity className="w-3 h-3 mr-1.5" /> Activity Log
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-leaderboard">
            <Trophy className="w-3 h-3 mr-1.5" /> Leaderboard
          </TabsTrigger>
          <TabsTrigger value="trades" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-trades">
            <Target className="w-3 h-3 mr-1.5" /> Trade Repository
          </TabsTrigger>
          <TabsTrigger value="scorecard" className="rounded-sm text-xs data-[state=active]:bg-[#f5b400]/10 data-[state=active]:text-[#f5b400]" data-testid="tab-scorecard">
            <BarChart3 className="w-3 h-3 mr-1.5" /> Scorecard
          </TabsTrigger>
        </TabsList>

        {/* Activity Log Tab */}
        <TabsContent value="activity" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Select value={String(logFilter.sinceHours)} onValueChange={v => setLogFilter(prev => ({ ...prev, sinceHours: parseInt(v) }))}>
                <SelectTrigger className="w-[120px] bg-[#0e0f12] border-white/10">
                  <SelectValue placeholder="Time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 1 hour</SelectItem>
                  <SelectItem value="6">Last 6 hours</SelectItem>
                  <SelectItem value="24">Last 24 hours</SelectItem>
                  <SelectItem value="168">Last 7 days</SelectItem>
                </SelectContent>
              </Select>
              <Select value={logFilter.level} onValueChange={v => setLogFilter(prev => ({ ...prev, level: v }))}>
                <SelectTrigger className="w-[120px] bg-[#0e0f12] border-white/10">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="DECISION">Decisions</SelectItem>
                  <SelectItem value="INFO">Info</SelectItem>
                  <SelectItem value="WARN">Warnings</SelectItem>
                  <SelectItem value="ERROR">Errors</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={loadActivityLogs} disabled={loading.logs}>
              <RefreshCw className={`w-3 h-3 mr-1.5 ${loading.logs ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          
          <ScrollArea className="h-[500px]">
            <div className="space-y-2">
              {activityLogs.map((log, idx) => {
                const Icon = TYPE_ICONS[log.type] || Activity;
                return (
                  <div key={log.id || idx} className="flex items-start gap-3 p-3 bg-[#14161a] border border-white/5 rounded-sm">
                    <Icon className={`w-4 h-4 mt-0.5 ${LEVEL_COLORS[log.level] || 'text-zinc-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className={`text-[9px] ${LEVEL_COLORS[log.level] || ''}`}>
                          {log.level}
                        </Badge>
                        <span className="text-[10px] text-zinc-500">{log.type?.replace(/_/g, ' ')}</span>
                        <span className="text-[10px] text-zinc-600 ml-auto">{formatTime(log.timestamp)}</span>
                      </div>
                      <p className="text-xs text-white truncate">{log.message}</p>
                      {log.metadata?.change_summary && (
                        <p className="text-[10px] text-[#f5b400] mt-1">{log.metadata.change_summary}</p>
                      )}
                    </div>
                  </div>
                );
              })}
              {activityLogs.length === 0 && (
                <div className="text-center py-8 text-zinc-500 text-sm">No activity logs found</div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Select value={leaderboardFilter.sortBy} onValueChange={v => setLeaderboardFilter(prev => ({ ...prev, sortBy: v }))}>
                <SelectTrigger className="w-[140px] bg-[#0e0f12] border-white/10">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="risk_reward_ratio">Risk/Reward</SelectItem>
                  <SelectItem value="confidence">Confidence</SelectItem>
                  <SelectItem value="created_at">Newest</SelectItem>
                </SelectContent>
              </Select>
              <Select value={leaderboardFilter.status} onValueChange={v => setLeaderboardFilter(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="w-[100px] bg-[#0e0f12] border-white/10">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={loadLeaderboard} disabled={loading.leaderboard}>
              <RefreshCw className={`w-3 h-3 mr-1.5 ${loading.leaderboard ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>

          <div className="bg-[#14161a] border border-white/5 rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/5 bg-white/2">
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa]">Rank</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa]">Symbol</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa]">Direction</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa]">RR Ratio</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa]">Confidence</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa]">Strategy</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((trade, idx) => (
                  <tr key={trade.id} className="border-b border-white/5 hover:bg-white/2">
                    <td className="px-3 py-2">
                      <span className={`font-mono ${idx < 3 ? 'text-[#f5b400]' : 'text-zinc-400'}`}>
                        #{idx + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-white font-mono">{trade.symbol}</td>
                    <td className="px-3 py-2">
                      <span className={`flex items-center gap-1 ${trade.direction === 'LONG' ? 'text-green-400' : 'text-red-400'}`}>
                        {trade.direction === 'LONG' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {trade.direction}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[#f5b400]">{trade.risk_reward_ratio?.toFixed(2) || '--'}x</td>
                    <td className="px-3 py-2 text-right font-mono text-white">{trade.confidence?.toFixed(0)}%</td>
                    <td className="px-3 py-2 text-zinc-400 truncate max-w-[150px]">{trade.strategy_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {leaderboard.length === 0 && (
              <div className="text-center py-8 text-zinc-500 text-sm">No trades found</div>
            )}
          </div>
        </TabsContent>

        {/* Trade Repository Tab */}
        <TabsContent value="trades" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Input 
                placeholder="Symbol..." 
                value={tradeFilter.symbol}
                onChange={e => setTradeFilter(prev => ({ ...prev, symbol: e.target.value }))}
                className="w-[100px] bg-[#0e0f12] border-white/10 text-xs"
              />
              <Select value={tradeFilter.status || 'all'} onValueChange={v => setTradeFilter(prev => ({ ...prev, status: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-[100px] bg-[#0e0f12] border-white/10">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tradeFilter.direction || 'all'} onValueChange={v => setTradeFilter(prev => ({ ...prev, direction: v === 'all' ? '' : v }))}>
                <SelectTrigger className="w-[100px] bg-[#0e0f12] border-white/10">
                  <SelectValue placeholder="Direction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="LONG">Long</SelectItem>
                  <SelectItem value="SHORT">Short</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportTrades}>
                <Download className="w-3 h-3 mr-1.5" /> Export
              </Button>
              <Button variant="outline" size="sm" onClick={loadAllTrades} disabled={loading.trades}>
                <RefreshCw className={`w-3 h-3 mr-1.5 ${loading.trades ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          <Card className="bg-[#14161a] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white flex items-center justify-between">
                <span>Paper Trades ({allTrades.total} total)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-2 py-2 text-[10px] uppercase text-[#a1a1aa]">Symbol</th>
                      <th className="text-left px-2 py-2 text-[10px] uppercase text-[#a1a1aa]">Dir</th>
                      <th className="text-right px-2 py-2 text-[10px] uppercase text-[#a1a1aa]">Entry</th>
                      <th className="text-right px-2 py-2 text-[10px] uppercase text-[#a1a1aa]">Target</th>
                      <th className="text-right px-2 py-2 text-[10px] uppercase text-[#a1a1aa]">Stop</th>
                      <th className="text-right px-2 py-2 text-[10px] uppercase text-[#a1a1aa]">Conf</th>
                      <th className="text-center px-2 py-2 text-[10px] uppercase text-[#a1a1aa]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTrades.trades?.map(trade => (
                      <tr key={trade.id} className="border-b border-white/5 hover:bg-white/2">
                        <td className="px-2 py-1.5 text-white font-mono">{trade.symbol}</td>
                        <td className="px-2 py-1.5">
                          <span className={trade.direction === 'LONG' ? 'text-green-400' : 'text-red-400'}>
                            {trade.direction?.charAt(0)}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{formatPrice(trade.entry_price)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-green-400">{formatPrice(trade.target_price)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-red-400">{formatPrice(trade.stop_loss)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-white">{trade.confidence?.toFixed(0)}%</td>
                        <td className="px-2 py-1.5 text-center">
                          <Badge variant="outline" className={`text-[9px] ${
                            trade.outcome === 'win' ? 'text-green-400 border-green-400/30' :
                            trade.outcome === 'loss' ? 'text-red-400 border-red-400/30' :
                            trade.status === 'open' ? 'text-blue-400 border-blue-400/30' : 'text-zinc-400'
                          }`}>
                            {trade.outcome || trade.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scorecard Tab */}
        <TabsContent value="scorecard" className="space-y-4">
          <div className="flex items-center justify-end">
            <Button variant="outline" size="sm" onClick={handleRefreshScorecard} disabled={loading.scorecard}>
              <RefreshCw className={`w-3 h-3 mr-1.5 ${loading.scorecard ? 'animate-spin' : ''}`} /> Refresh Scorecard
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Overall Stats */}
            <Card className="bg-[#14161a] border-white/5">
              <CardHeader>
                <CardTitle className="text-sm text-white">Overall Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] uppercase text-[#a1a1aa]">Win Rate</p>
                    <p className={`text-xl font-bold ${winRateClass}`} data-testid="overall-win-rate">
                      {winRate}%
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      of {decidedCount} decided (W+L)
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#a1a1aa]">Expired</p>
                    <p className="text-xl font-bold text-zinc-400" data-testid="overall-expired">
                      {expiredCount}
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      not in win rate
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#a1a1aa]">Wins</p>
                    <p className="text-xl font-bold text-green-400">{scorecard?.summary?.wins || 0}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#a1a1aa]">Losses</p>
                    <p className="text-xl font-bold text-red-400">{scorecard?.summary?.losses || 0}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#a1a1aa]">Resolved</p>
                    <p className="text-xl font-bold text-white">{scorecard?.summary?.resolved_trades || 0}</p>
                    <p className="text-[10px] text-zinc-500">W+L+expired</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#a1a1aa]">Decided</p>
                    <p className="text-xl font-bold text-white">{decidedCount}</p>
                    <p className="text-[10px] text-zinc-500">W+L only</p>
                  </div>
                </div>
                {decidedCount < (summary?.min_decided_for_signal ?? MIN_DECIDED_FOR_SIGNAL) && (
                  <p className="text-[10px] text-zinc-500 border-t border-white/5 pt-2">
                    Small sample ({decidedCount} decided) — rate shown neutral until {summary?.min_decided_for_signal ?? MIN_DECIDED_FOR_SIGNAL} decided trades.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* High Confidence Performance */}
            <Card className="bg-[#14161a] border-white/5">
              <CardHeader>
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#f5b400]" />
                  High Confidence Trades (≥80%)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  const hc = scorecard?.high_confidence_performance;
                  const hcDecided = hc?.decided ?? ((hc?.wins || 0) + (hc?.losses || 0));
                  const hcRate = hc?.win_rate ?? 0;
                  const hcClass = winRateColorClass(hcRate, hcDecided, summary?.min_decided_for_signal ?? MIN_DECIDED_FOR_SIGNAL);
                  return (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] uppercase text-[#a1a1aa]">Decided</p>
                          <p className="text-xl font-bold text-white">{hcDecided}</p>
                          <p className="text-[10px] text-zinc-500">
                            {(hc?.wins || 0)}W / {(hc?.losses || 0)}L
                            {(hc?.expired || 0) > 0 ? ` · ${hc.expired} expired` : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase text-[#a1a1aa]">Win Rate</p>
                          <p className={`text-xl font-bold ${hcClass}`}>{hcRate}%</p>
                          <p className="text-[10px] text-zinc-500">decided only</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-zinc-500">
                        Win rate excludes expired. Expired high-conf trades are timed out, not losses.
                      </p>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Strategy Breakdown */}
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader>
              <CardTitle className="text-sm text-white">Strategy Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {/* Column header — fixed grid kills the empty gap between name and W/L */}
                <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_3.5rem] gap-2 px-2 pb-1 text-[10px] uppercase tracking-wider text-zinc-500">
                  <span>Strategy</span>
                  <span className="text-right">W / L</span>
                  <span className="text-right">Expired</span>
                  <span className="text-right">Rate</span>
                </div>
                {scorecard?.strategy_breakdown?.map((strategy, idx) => {
                  const sDecided = strategy.decided ?? ((strategy.wins || 0) + (strategy.losses || 0));
                  const sExpired = strategy.expired ?? 0;
                  const sRateClass = winRateColorClass(
                    strategy.win_rate,
                    sDecided,
                    summary?.min_decided_for_signal ?? MIN_DECIDED_FOR_SIGNAL
                  );
                  return (
                    <div
                      key={strategy.strategy || idx}
                      className="grid grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_3.5rem] gap-2 items-center p-2 bg-white/2 rounded-sm"
                    >
                      <div className="min-w-0">
                        <p className="text-xs text-white truncate">{strategy.strategy || 'Unknown'}</p>
                        <p className="text-[10px] text-zinc-500">
                          {sDecided} decided{sExpired > 0 ? ` · ${sExpired} expired` : ''}
                        </p>
                      </div>
                      <div className="text-right font-mono text-xs">
                        <span className="text-green-400">{strategy.wins ?? 0}</span>
                        <span className="text-zinc-500"> / </span>
                        <span className="text-red-400">{strategy.losses ?? 0}</span>
                      </div>
                      <div className="text-right font-mono text-xs text-zinc-400">
                        {sExpired}
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-mono ${sRateClass}`}>
                          {strategy.win_rate ?? 0}%
                        </p>
                      </div>
                    </div>
                  );
                })}
                {(!scorecard?.strategy_breakdown || scorecard.strategy_breakdown.length === 0) && (
                  <p className="text-center text-zinc-500 text-sm py-4">No strategy data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top RR Winners */}
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader>
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#f5b400]" />
                Top Risk/Reward Winners
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {scorecard?.top_rr_winners?.slice(0, 10).map((trade, idx) => (
                  <div key={trade.id} className="flex items-center justify-between p-2 bg-white/2 rounded-sm">
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-sm ${idx < 3 ? 'text-[#f5b400]' : 'text-zinc-400'}`}>#{idx + 1}</span>
                      <div>
                        <p className="text-xs text-white font-mono">{trade.symbol}</p>
                        <p className="text-[10px] text-zinc-500">{trade.strategy_label}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono text-[#f5b400]">{trade.risk_reward_ratio?.toFixed(2)}x RR</p>
                      <p className="text-[10px] text-zinc-400">{trade.confidence?.toFixed(0)}% conf</p>
                    </div>
                  </div>
                ))}
                {(!scorecard?.top_rr_winners || scorecard.top_rr_winners.length === 0) && (
                  <p className="text-center text-zinc-500 text-sm py-4">No winning trades yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
